import React, { useState } from 'react';
import {
  Box,
  TextField,
  Autocomplete,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Tabs,
  Tab,
  Dialog,
  DialogContent,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { Info, CheckCircle } from '@mui/icons-material'; // Add CheckCircle
import jsPDF from 'jspdf';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';

const sourceOptions = ['MySQL', 'Microsoft SQL Server', 'Oracle', 'Amazon RDS', 'SQLite', 'MongoDB', 'IBM Db2'];
const targetOptions = ['Postgresql'];

const DDL = () => {
  const [tabValue, setTabValue] = useState(0);
  const [sourceDb, setSourceDb] = useState('');
  const [targetDb, setTargetDb] = useState('');
  const [sourceSearch, setSourceSearch] = useState('');
  const [targetSearch, setTargetSearch] = useState('');
  const [fileName, setFileName] = useState('');
  const [ddlFile, setDdlFile] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({
    testScript: null, // <-- Change default from false to null
    testData: null,   // <-- Change default from false to null
    documentation: null, // <-- Change default from false to null
  });
  const [specialInstruction, setSpecialInstruction] = useState('');
  const [showSpinnerModal, setShowSpinnerModal] = useState(false);
  const [progress, setProgress] = useState(0);
  const [apiResult, setApiResult] = useState(null);
  const [isFinalSubmit, setIsFinalSubmit] = useState(false);

  const handleTabChange = (event, newValue) => setTabValue(newValue);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setFileName(file.name);
      setDdlFile(file);

      // Upload the file to backend
      const formData = new FormData();
      formData.append('file', file);

      try {
        await fetch('https://api.elevate.de.ltim.io/modernization_task_file_upload/', {
          method: 'POST',
          body: formData,
        });
      } catch (error) {
        alert(`Error uploading ${file.name}: ${error.message}`);
      }
    }
  };

  

  const handleSubmit = async () => {
    if (!fileName || !sourceDb || !targetDb) return;

    setIsFinalSubmit(true);
    setShowSpinnerModal(true);
    setProgress(0);

    // Simulate progress
    let interval=null;
    interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + 10;
      });
    }, 25000);

    // Prepare payload as per your requirement
    const payload = {
      file_name: fileName,
      is_test_script: selectedOptions.testScript === true,
      is_test_data: selectedOptions.testData === true,
      instruction: specialInstruction,
      source: sourceDb,
      target: targetDb,
      is_conversion: true,
      is_ddl: true,
      migration_type: "", // Set as needed
      task: [], // Set as needed
      is_documentation: selectedOptions.documentation === true,
    };

    try {
      const response = await fetch('https://api.elevate.de.ltim.io/modernization_task/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      clearInterval(interval);
      setProgress(100);

      if (!response.ok) {
        const errorData = await response.json();
        alert(`Failed to process DDL: ${errorData.detail}`);
        setShowSpinnerModal(false);
      } else {
        const data = await response.json();
        setApiResult(data);
        setTimeout(() => {
          setShowSpinnerModal(false);
          setTabValue(1);
        }, 2000);
      }
    } catch (error) {
      clearInterval(interval);
      setShowSpinnerModal(false);
      alert(`Error processing DDL: ${error.message}`);
    }
  };

  const handleDownloadPDF = () => {
   if (!apiResult || !apiResult.report) {
         alert('No API result available to download.');
         return;
       }
   
       // Create a new jsPDF instance
       const doc = new jsPDF();
   
       // Add a title to the PDF
       doc.setFontSize(16);
       doc.text('Output', 10, 10);
   
       // Extract the report content
       const resultContent = String(apiResult.report);
   
       // Add the content to the PDF with proper formatting
       doc.setFontSize(11);
       const pageWidth = doc.internal.pageSize.width;
       const margin = 10;
       const contentWidth = pageWidth - margin * 2;
   
       // Split the content into lines and handle page breaks
       const lines = doc.splitTextToSize(resultContent, contentWidth);
       let cursorY = 20;
       const lineHeight = 7;
       const pageHeight = doc.internal.pageSize.height;
   
       lines.forEach((line) => {
         if (cursorY + lineHeight > pageHeight - margin) {
           doc.addPage();
           cursorY = margin;
         }
         doc.text(line, margin, cursorY);
         cursorY += lineHeight;
       });
   
       // Save the PDF
       doc.save('Output.pdf');
  };

  const handleCopyCode = () => {
    if (!apiResult || !apiResult.report) return;
    navigator.clipboard.writeText(String(apiResult.report));
  };

  const handleDownloadTestData = () => {
  if (!apiResult) {
      alert('No API result available.');
      return;
    }
    if (!Array.isArray(apiResult.links) || apiResult.links.length === 0) {
      alert('No test data links available to download.');
      return;
    }
    // Download each file sequentially
    const downloadFile = (index) => {
      if (index >= apiResult.links.length) return;
      const link = apiResult.links[index];
      if (typeof link === 'string' && link.trim() !== '') {
        const anchor = document.createElement('a');
        anchor.href = link;
        anchor.target = '_self';
        anchor.download = '';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        setTimeout(() => downloadFile(index + 1), 1000);
      } else {
        downloadFile(index + 1);
      }
    };
    downloadFile(0);
};

  return (
    <Box sx={{ width: '100%', marginTop: -7 }}>
      {/* Tabs styled and behaving like Scheduler */}
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        sx={{
          pl: 0,
          '& .MuiTabs-flexContainer': { justifyContent: 'flex-start' },
          '& .MuiTab-root': {
            minWidth: 120,
            fontWeight: 'bold',
            textTransform: 'none',
            paddingRight: 0,
            pl: 0,
          },
        }}
        TabIndicatorProps={{ style: { display: 'none' } }}
      >
        <Tab
          label={
            <Box display="flex" alignItems="center">
              {/* 1 Input */}
              <Box display="flex" alignItems="center">
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width={24}
                  height={24}
                  borderRadius="50%"
                  bgcolor={tabValue === 0 ? "#1976d2" : (apiResult?.report ? "#4caf50" : "white")}
                  border="1.5px solid"
                  borderColor={tabValue === 0 ? "#1976d2" : (apiResult?.report ? "#4caf50" : "black")}
                  mr={1}
                  transition="all 0.2s"
                >
                  {apiResult?.report && tabValue !== 0 ? (
                    <CheckCircle sx={{ color: "white", fontSize: 20 }} />
                  ) : (
                    <Typography variant="subtitle2" sx={{ color: tabValue === 0 ? "white" : "black", fontWeight: 600 }}>1</Typography>
                  )}
                </Box>
                <Typography
                  sx={{
                    fontWeight: tabValue === 0 ? 700 : 400,
                    color: tabValue === 0 ? "primary.main" : "black",
                    userSelect: "none",
                    transition: "all 0.2s"
                  }}
                >
                  Input
                </Typography>
              </Box>
              {/* Arrow */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: 4,
                  ml: 1,
                  mr: 1,
                  mb: 0.6,
                }}
              >
                <Typography sx={{ color: "#bdbdbd", fontWeight: 700, fontSize: 44, lineHeight: 1 }}>
                  &rarr;
                </Typography>
              </Box>
            </Box>
          }
          disableRipple
          style={{ minWidth: 0, paddingRight: 0, paddingLeft: 0 }}
        />
        <Tab
          label={
            <Box display="flex" alignItems="center">
              {/* 2 Result */}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={24}
                height={24}
                borderRadius="50%"
                bgcolor={tabValue === 1 ? "#1976d2" : "white"}
                border="1.5px solid"
                borderColor={tabValue === 1 ? "#1976d2" : "black"}
                mr={1}
                transition="all 0.2s"
              >
                <Typography variant="subtitle2" sx={{ color: tabValue === 1 ? "white" : "black", fontWeight: 600 }}>2</Typography>
              </Box>
              <Typography
                sx={{
                  fontWeight: tabValue === 1 ? 700 : 400,
                  color: tabValue === 1 ? "primary.main" : "black",
                  userSelect: "none",
                  transition: "all 0.2s"
                }}
              >
                Result
              </Typography>
            </Box>
          }
          disableRipple
          style={{ minWidth: 0, paddingLeft: 0 }}
        />
      </Tabs>

      {/* Input Tab Content */}
      {tabValue === 0 && (
        <Box sx={{ marginTop: 2 }}>
          {/* Source and Target Dropdowns in a Row - styled like dbMigration */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            {/* Source Dropdown */}
            <Box sx={{ maxWidth: '300px' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Source<span style={{ color: 'red' }}>*</span>
              </Typography>
              <Autocomplete
                options={sourceOptions}
                value={sourceDb || ''}
                onChange={(_, newValue) => setSourceDb(newValue || '')}
                inputValue={sourceSearch}
                onInputChange={(_, newInputValue) => setSourceSearch(newInputValue)}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select Source" variant="outlined" size="small" />
                )}
                sx={{ width: 300 }}
              />
            </Box>
            {/* Target Dropdown */}
            <Box sx={{ maxWidth: '300px', marginRight: { md: '150px', xs: 0 }, marginTop: { xs: 2, md: 0 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Target<span style={{ color: 'red' }}>*</span>
              </Typography>
              <Autocomplete
                options={targetOptions}
                value={targetDb || ''}
                onChange={(_, newValue) => setTargetDb(newValue || '')}
                inputValue={targetSearch}
                onInputChange={(_, newInputValue) => setTargetSearch(newInputValue)}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select Target" variant="outlined" size="small" />
                )}
                sx={{ width: 300 }}
              />
            </Box>
          </Box>

          {/* Upload Button - only show after both dropdowns have values */}
          {(sourceDb && targetDb) && (
            <Box sx={{ marginBottom: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: 1,marginTop:2 }}>
                Please upload DDL file<span style={{ color: 'red' }}>*</span>
              </Typography>
              <Button
                variant="contained"
                color="primary"
                component="label"
                sx={{
                  backgroundColor: 'white',
                  color: 'black',
                  border: '1px solid black',
                  boxShadow: 'none',
                  height: '40px',
                }}
              >
                Upload DDL
                <input type="file" hidden onChange={handleFileUpload} />
              </Button>
              {fileName && (
                <Typography variant="body2" sx={{ marginTop: 1, color: 'green', fontWeight: 'bold' }}>
                  Uploaded File: {fileName}
                </Typography>
              )}
            </Box>
          )}

          {/* Test Script Option - only show after file is uploaded */}
          {fileName && (
            <Box sx={{ marginBottom: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                Do you want to generate the test script?
              </Typography>
              <RadioGroup
                row
                value={
                  selectedOptions.testScript === true
                    ? 'Yes'
                    : selectedOptions.testScript === false
                    ? 'No'
                    : ''
                }
                onChange={(event) => {
                  const value = event.target.value;
                  setSelectedOptions((prev) => ({
                    ...prev,
                    testScript: value === 'Yes' ? true : value === 'No' ? false : null,
                    // Reset testData if testScript is set to No
                    testData: value === 'No' ? null : prev.testData,
                  }));
                }}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </Box>
          )}

          {/* Test Data Option - show if test script option is selected as Yes or No */}
          {fileName && selectedOptions.testScript !== null && (
            <Box sx={{ marginBottom: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                Do you want to generate the relevant test data also?
              </Typography>
              <RadioGroup
                row
                value={
                  selectedOptions.testData === true
                    ? 'Yes'
                    : selectedOptions.testData === false
                    ? 'No'
                    : ''
                }
                onChange={(event) => {
                  const value = event.target.value;
                  setSelectedOptions((prev) => ({
                    ...prev,
                    testData: value === 'Yes' ? true : value === 'No' ? false : null,
                  }));
                }}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
              {selectedOptions.testData === true && (
                <Box sx={{ marginTop: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', marginRight: 1 }}>
                      Special Instruction for Test Data
                    </Typography>
                    <Tooltip title="Generate 50 rows of data" arrow>
                      <Info sx={{ color: 'gray', cursor: 'pointer' }} />
                    </Tooltip>
                  </Box>
                  <TextField
                    multiline
                    variant="outlined"
                    size='small'
                    value={specialInstruction}
                    onChange={(e) => setSpecialInstruction(e.target.value)}
                    sx={{ width: '250px' }}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Documentation Option - show if test data option is selected as Yes or No */}
          {fileName && selectedOptions.testData !== null && (
            <Box sx={{ marginBottom: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                Documentation Required?
              </Typography>
              <RadioGroup
                row
                value={
                  selectedOptions.documentation === true
                    ? 'Yes'
                    : selectedOptions.documentation === false
                    ? 'No'
                    : ''
                }
                onChange={(event) => {
                  const value = event.target.value;
                  setSelectedOptions((prev) => ({
                    ...prev,
                    documentation: value === 'Yes' ? true : value === 'No' ? false : null,
                  }));
                }}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </Box>
          )}

          {/* Submit Button - show if documentation option is selected as Yes or No */}
          {fileName && selectedOptions.documentation !== null && (
            <Box sx={{ marginTop: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                sx={{
                  backgroundColor: 'white',
                  color: 'black',
                  border: '1px solid black',
                  boxShadow: 'none',
                }}
                disabled={!fileName}
              >
                Submit
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Result Tab Content */}
     {tabValue === 1 && isFinalSubmit && (
        <Box sx={{ marginTop: 4 }}>
          <Box
            sx={{
              marginTop: 4,
              padding: '16px',
              backgroundColor: '#f9f9f9',
              borderRadius: '8px',
              border: '1px solid #ccc',
              marginBottom: 2,
            }}
          >
            <Typography variant="h6" sx={{ marginBottom: 2 }}>
              Output
            </Typography>
            {apiResult?.report ? (
              <Typography
                variant="body1"
                sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
              >
                {String(apiResult.report)}
              </Typography>
            ) : (
              <Typography variant="body2">No result available</Typography>
            )}
          </Box>
          {/* Download PDF and Copy Code buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              gap: 2,
              marginTop: 2,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleDownloadPDF}
              sx={{
                backgroundColor: 'white',
                color: 'black',
                border: '1px solid black',
                boxShadow: 'none',
              }}
              startIcon={<DownloadIcon />}
            >
              Download PDF
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCopyCode}
              sx={{
                backgroundColor: 'white',
                color: 'black',
                border: '1px solid black',
                boxShadow: 'none',
              }}
              startIcon={<ContentCopyIcon />}
            >
              Copy Code
            </Button>
            {/* Download Test Data Button */}
            {apiResult?.links && apiResult.links.length > 0 && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleDownloadTestData}
                sx={{
                  backgroundColor: 'white',
                  color: 'black',
                  border: '1px solid black',
                  boxShadow: 'none',
                }}
                startIcon={<DownloadIcon />}
              >
                Download Test Data
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* Spinner Modal */}
      <Dialog open={showSpinnerModal} onClose={() => setShowSpinnerModal(false)}>
        <DialogContent sx={{ textAlign: 'center', padding: 4 }}>
          {progress < 100 ? (
            <>
              <CircularProgress />
              <Typography variant="h6" sx={{ marginTop: 2 }}>
                Document is being generated...
              </Typography>
              <Typography variant="body1" sx={{ marginTop: 1 }}>
                {progress}%
              </Typography>
            </>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  backgroundColor: 'green',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="h4" sx={{ color: 'white' }}>✔</Typography>
              </Box>
              <Typography variant="h6" sx={{ marginTop: 2 }}>
                Document Generated Successfully!
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DDL;
// ...end of file...